import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { ErrorState, SkeletonRows } from '@/components/admin/states';
import {
  addMemberMeetEmail,
  deleteMemberMeetEmail,
  fetchMemberMeetEmails,
  MEET_EMAIL_LIMIT,
  setMemberMeetEmailVerified,
} from '@/features/members/api';
import { ApiError } from '@/lib/api/http';

/**
 * Meet参加ログの突合に使う確認済みメール（事務局のみ / implementation-spec §8・D-063）。
 * ここに登録したメールと参加ログのメールが完全一致したときだけ自動で出席になる。
 */
export function MemberMeetEmailsBox({ memberId }: { memberId: string }): ReactElement {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['member-meet-emails', memberId],
    queryFn: () => fetchMemberMeetEmails(memberId),
  });

  function invalidate(): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: ['member-meet-emails', memberId] });
  }

  function onError(err: unknown): void {
    setError(err instanceof ApiError ? err.message : '操作に失敗しました。');
  }

  const addMutation = useMutation({
    mutationFn: (email: string) => addMemberMeetEmail(memberId, email, true),
    onSuccess: async () => {
      setDraft('');
      setError(null);
      await invalidate();
    },
    onError,
  });

  const toggleMutation = useMutation({
    mutationFn: (input: { id: string; verified: boolean }) =>
      setMemberMeetEmailVerified(memberId, input.id, input.verified),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteMemberMeetEmail(memberId, id),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError,
  });

  if (query.isPending) return <SkeletonRows rows={2} />;
  if (query.isError) {
    return (
      <ErrorState
        message="Meet用メールの取得に失敗しました。"
        onRetry={() => void query.refetch()}
      />
    );
  }

  const rows = query.data;
  const full = rows.length >= MEET_EMAIL_LIMIT;
  const busy = addMutation.isPending || toggleMutation.isPending || deleteMutation.isPending;

  return (
    <MetaBox title="Meet用メール">
      <p className="mb-2 text-wptext-muted">
        Google Meetの参加ログと突合するためのメールアドレスです（1人{MEET_EMAIL_LIMIT}件まで）。
        ここに登録された「確認済み」のメールと参加ログが<strong>完全に一致</strong>したときだけ、
        出欠が自動でオンライン参加になります。一致しなかった参加者はイベントの「Meet資料」タブの
        確認キューに入り、事務局が手動で確定します。
      </p>

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      {rows.length === 0 ? (
        <p className="mb-2 text-wptext-muted">まだ登録されていません。</p>
      ) : (
        <ul className="mb-2 flex flex-col gap-1">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-2">
              <span className="text-wptext-strong">{row.email}</span>
              <span
                className={
                  row.verified
                    ? 'rounded-sm bg-wpnotice-success/15 px-2 py-0.5 text-wptext-strong'
                    : 'rounded-sm bg-wpborder/50 px-2 py-0.5 text-wptext-muted'
                }
              >
                {row.verified ? '確認済み' : '未確認（突合に使われません）'}
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  toggleMutation.mutate({ id: row.id, verified: !row.verified })
                }
                className="text-wpaccent hover:underline disabled:opacity-50"
              >
                {row.verified ? '未確認に戻す' : '確認済みにする'}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => deleteMutation.mutate(row.id)}
                className="text-wpnotice-error hover:underline disabled:opacity-50"
              >
                削除
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim()) addMutation.mutate(draft.trim());
        }}
      >
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">メールアドレスを追加</span>
          <input
            type="email"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={full || busy}
            placeholder="meet@example.com"
            className="min-w-[16rem] rounded-sm border border-wpborder px-3 py-2 disabled:bg-wpcanvas"
          />
        </label>
        <button
          type="submit"
          disabled={full || busy || !draft.trim()}
          className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
        >
          追加する
        </button>
        {full && (
          <span className="text-wptext-muted">
            {MEET_EMAIL_LIMIT}件登録済みです。追加するにはどれかを削除してください。
          </span>
        )}
      </form>
    </MetaBox>
  );
}
